// Shared helpers + seed data for the Short Courses "Available Subjects"
// section (ShortCoursesList.jsx), where every subject is listed with its own
// Overview/Courses tabs. Mirrors the Cisco career-paths pattern in
// utils/ciscoCareerPaths.js: one JSON blob on a dedicated Program record
// (slug "short-courses-catalog", repurposing the unused ciscoCcnpSection
// field), parsed here, with CMS values always overriding this seed data.

export function slugify(name) {
  return (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

// Parses the JSON stored in program.ciscoCcnpSection (repurposed — that field
// was legacy/unused; not related to the actual Cisco Networking Academy
// program, which is a different Program record entirely).
// Expects { categories: [{ slug, name, tag, heroIntro, badge_image, courses: [...] }] }
export function parseCatalog(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.categories)) return parsed.categories;
  } catch {}
  return null;
}

// Seed content so the pages are populated before an admin has entered real
// copy via the CMS. Any field the CMS provides overrides these defaults.
export const SHORT_COURSE_CATEGORIES_SEED = [
  {
    slug: "business-management",
    name: "Business Management",
    tag: "Short Course",
    badge_image: "",
    heroIntro: "Build practical business and accounting skills employers actually use — from bookkeeping software to digital marketing.",
    courses: [
      { slug: "basic-account-with-it", name: "Basic Account with IT", overview: "Learn core accounting principles alongside the IT tools used to manage business finances day to day.", mode: "In Class", duration: "8 Weeks", hours: "80 Hours", fee: "350,000 TZS" },
      { slug: "tally", name: "Tally", overview: "Master Tally, one of the most widely used accounting software packages for small and medium businesses.", mode: "In Class", duration: "8 Weeks", hours: "80 Hours", fee: "350,000 TZS" },
      { slug: "quickbook", name: "QuickBook", overview: "Learn to manage invoicing, payroll, and financial reporting using QuickBooks, a leading accounting platform.", mode: "In Class", duration: "8 Weeks", hours: "80 Hours", fee: "350,000 TZS" },
      { slug: "zoho", name: "Zoho", overview: "Get hands-on with Zoho's business suite — covering finance, CRM, and daily operations management.", mode: "In Class", duration: "8 Weeks", hours: "80 Hours", fee: "350,000 TZS" },
      { slug: "digital-marketing", name: "Digital Marketing", overview: "Learn to plan and run digital marketing campaigns — social media, content, SEO, and paid advertising basics.", mode: "In Class", duration: "8 Weeks", hours: "80 Hours", fee: "350,000 TZS" },
    ],
  },
  {
    slug: "ai-graphics-multimedia",
    name: "AI Powered Graphics and Multimedia Production",
    tag: "Short Course",
    badge_image: "",
    heroIntro: "A three-level pathway into AI-assisted graphic design and multimedia production, from first principles to advanced production work.",
    courses: [
      { slug: "beginner", name: "Beginner", overview: "Start creating graphics and multimedia content with the help of AI-powered design tools.", mode: "In Class", duration: "2 Months", hours: "80 Hours", fee: "405,000 TZS" },
      { slug: "intermediate", name: "Intermediate", overview: "Build on the fundamentals with more advanced AI design workflows and multimedia techniques.", mode: "In Class", duration: "2 Months", hours: "80 Hours", fee: "500,000 TZS" },
      { slug: "advanced", name: "Advanced", overview: "Produce professional-grade graphics and multimedia projects using advanced AI production techniques.", mode: "In Class", duration: "2 Months", hours: "80 Hours", fee: "655,000 TZS" },
    ],
  },
  {
    slug: "content-creation-with-ai",
    name: "Content Creation with AI",
    tag: "Short Course",
    badge_image: "",
    heroIntro: "Hands-on training across today's leading AI content creation tools — design, video, and 3D.",
    courses: [
      { slug: "canva-pro", name: "Canva Pro", overview: "Learn to design social media graphics, presentations, and marketing materials quickly using Canva Pro.", mode: "In Class", duration: "2 Weeks", hours: "20 Hours", fee: "150,000 TZS" },
      { slug: "jitter-video", name: "Jitter Video", overview: "Create polished motion graphics and animated video content using Jitter.", mode: "In Class", duration: "2 Weeks", hours: "20 Hours", fee: "150,000 TZS" },
      { slug: "veo-ai", name: "Veo AI", overview: "Learn AI-powered video generation with Veo AI to produce high-quality video content from simple prompts.", mode: "In Class", duration: "2 Weeks", hours: "20 Hours", fee: "150,000 TZS" },
      { slug: "capcut-pro", name: "CapCut Pro", overview: "Master fast, professional video editing for social media and marketing using CapCut Pro.", mode: "In Class", duration: "2 Weeks", hours: "20 Hours", fee: "150,000 TZS" },
      { slug: "meshy-ai", name: "Meshy AI", overview: "Generate 3D models and assets from text or images using Meshy AI — ideal for design and content creators.", mode: "In Class", duration: "2 Weeks", hours: "20 Hours", fee: "200,000 TZS" },
    ],
  },
  {
    slug: "wase-programmes",
    name: "WASE Programmes",
    tag: "WASE",
    badge_image: "",
    heroIntro: "Combined computer-literacy programmes for Wahitimu wa Secondary (WASE) learners — pairing core computer skills with a focus area of your choice.",
    courses: [
      { slug: "computer-and-ai", name: "Computer and AI", overview: "Build foundational computer literacy alongside an introduction to artificial intelligence tools and concepts.", mode: "In Class", duration: "3 Months", hours: "100 Hours", fee: "360,000 TZS" },
      { slug: "computer-and-graphics", name: "Computer and Graphics", overview: "Combine core computer skills with graphic design fundamentals.", mode: "In Class", duration: "3 Months", hours: "100 Hours", fee: "475,000 TZS" },
      { slug: "computer-and-website", name: "Computer and Website", overview: "Combine core computer skills with the basics of building and publishing websites.", mode: "In Class", duration: "3 Months", hours: "100 Hours", fee: "425,000 TZS" },
      { slug: "computer-and-programming", name: "Computer and Programming", overview: "Combine core computer skills with an introduction to programming fundamentals.", mode: "In Class", duration: "3 Months", hours: "100 Hours", fee: "425,000 TZS" },
      { slug: "computer-and-electronics", name: "Computer and Electronics", overview: "Combine core computer skills with foundational electronics knowledge.", mode: "In Class", duration: "3 Months", hours: "100 Hours", fee: "425,000 TZS" },
      { slug: "computer-and-english", name: "Computer and English", overview: "Combine core computer skills with English language proficiency development.", mode: "In Class", duration: "3 Months", hours: "100 Hours", fee: "275,000 TZS" },
    ],
  },
  {
    slug: "programming",
    name: "Programming",
    tag: "Short Course",
    badge_image: "",
    heroIntro: "Learn Python, JavaScript, and C++ fundamentals through a structured, three-level programming pathway.",
    courses: [
      { slug: "beginner", name: "Beginner", overview: "Start your programming journey with the fundamentals of Python, JavaScript, and C++.", mode: "Blended", duration: "6 Weeks", hours: "30 Hours", fee: "350,000 TZS" },
      { slug: "intermediate", name: "Intermediate", overview: "Build on the fundamentals with more advanced concepts and hands-on programming projects.", mode: "Blended", duration: "6 Weeks", hours: "40 Hours", fee: "400,000 TZS" },
      { slug: "advanced", name: "Advanced", overview: "Master advanced programming techniques and prepare for real-world software development roles.", mode: "Blended", duration: "6 Weeks", hours: "40 Hours", fee: "450,000 TZS" },
    ],
  },
  {
    slug: "website-design",
    name: "Website Design",
    tag: "Short Course",
    badge_image: "",
    heroIntro: "Learn to design and build modern websites through a structured, three-level pathway — from your first web page to advanced, professional web design.",
    courses: [
      { slug: "beginner", name: "Beginner", overview: "Start building websites from scratch — HTML, CSS, and the fundamentals of web design.", mode: "In Class", duration: "2 Months", hours: "80 Hours", fee: "300,000 TZS" },
      { slug: "intermediate", name: "Intermediate", overview: "Build on the fundamentals with responsive design, layout techniques, and interactive components.", mode: "In Class", duration: "2 Months", hours: "80 Hours", fee: "350,000 TZS" },
      { slug: "advanced", name: "Advanced", overview: "Master advanced website design and prepare for professional web design and front-end roles.", mode: "In Class", duration: "2 Months", hours: "80 Hours", fee: "400,000 TZS" },
    ],
  },
];

// Merge CMS-saved categories over the seed defaults. Display order follows
// the saved array (not SHORT_COURSE_CATEGORIES_SEED) so an admin's Move
// Up/Down rearranging in the CMS is reflected here — every CMS save persists
// the complete, currently-ordered list. Missing fields on a saved category
// still fall back to its seed counterpart by slug; a saved category with no
// seed counterpart (added via the CMS's "Add Subject" button) is kept as-is.
export function getCatalog(rawJson) {
  const saved = parseCatalog(rawJson);
  if (!saved || saved.length === 0) return SHORT_COURSE_CATEGORIES_SEED;

  const seedBySlug = new Map(SHORT_COURSE_CATEGORIES_SEED.map((c) => [c.slug, c]));

  // Falls back to slugifying the name for saves made with a blank "URL Slug"
  // field — otherwise a category with no slug would silently never appear here.
  return saved
    .filter((c) => c.slug || c.name)
    .map((savedCat) => {
      const slug = savedCat.slug || slugify(savedCat.name || "");
      const seedCat = seedBySlug.get(slug);
      if (!seedCat) {
        return {
          slug,
          name: savedCat.name || "",
          tag: savedCat.tag || "",
          badge_image: savedCat.badge_image || "",
          heroIntro: savedCat.heroIntro || "",
          courses: Array.isArray(savedCat.courses) ? savedCat.courses.map(normalizeCourse) : [],
        };
      }
      return {
        ...seedCat,
        ...savedCat,
        slug,
        courses: Array.isArray(savedCat.courses) && savedCat.courses.length > 0
          ? savedCat.courses.map(normalizeCourse)
          : seedCat.courses,
      };
    });
}

function normalizeCourse(c) {
  return {
    slug: c.slug || "", name: c.name || "", overview: c.overview || "",
    mode: c.mode || "", duration: c.duration || "", hours: c.hours || "", fee: c.fee || "",
    image: c.image || "",
  };
}
